import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/useAuthStore';
import {
  Users, Shield, Activity, Truck, Briefcase, ChevronRight,
  Save, Eye, EyeOff, X, RefreshCw, Search
} from 'lucide-react';

// ─── CONFIGURACIÓN DE PERMISOS ──────────────────────────────────────────────
const PERMISSION_GROUPS = [
  {
    id: 'vistas',
    label: '👁️ Vistas del Panel',
    description: 'Qué tabs puede ver en el módulo de Operaciones',
    permissions: [
      { key: 'can_view_dispatch',     label: 'Despacho Diario',        desc: 'Ver y gestionar el despacho de viajes' },
      { key: 'can_view_control_room', label: 'Sala de Control',         desc: 'Monitor en tiempo real de la flota' },
      { key: 'can_view_fleet',        label: 'Monitor de Flota',        desc: 'GPS y posiciones en vivo' },
      { key: 'can_view_insights',     label: 'Insights',                desc: 'Dashboard de análisis y KPIs' },
      { key: 'can_view_billing',      label: 'Facturación',             desc: 'Gestión de pre-facturas y facturas' },
      { key: 'can_view_settlements',  label: 'Liquidaciones',           desc: 'Liquidación de conductores' },
      { key: 'can_view_reports',      label: 'Reportes',                desc: 'Reportes generales de operación' },
      { key: 'can_view_clients',      label: 'Clientes',                desc: 'Gestión de clientes y empresas' },
      { key: 'can_view_drivers',      label: 'Conductores (ver)',       desc: 'Ver listado de conductores' },
      { key: 'can_view_fleet_mgmt',   label: 'Gestión de Flota',        desc: 'Ver/editar móviles, conductores y contratos' },
      { key: 'can_view_tariffs',      label: 'Tarifas',                 desc: 'Configuración de tarifas' },
      { key: 'can_view_admin_trips',  label: 'Admin Viajes',            desc: 'Panel de administración de viajes' },
      { key: 'can_view_booking_sim',  label: 'Booking Simulator',       desc: 'Simulador de reservas Booking.com' },
    ],
  },
  {
    id: 'acciones',
    label: '✍️ Acciones Operativas',
    description: 'Qué puede hacer dentro de cada sección',
    permissions: [
      { key: 'can_create_trips',     label: 'Crear Viajes',           desc: 'Crear nuevos servicios/viajes' },
      { key: 'can_assign_vehicles',  label: 'Asignar Vehículos',      desc: 'Asignar móviles a conductores' },
      { key: 'can_process_vouchers', label: 'Procesar Vouchers',      desc: 'Procesar comprobantes en el historial' },
      { key: 'can_void_trips',       label: 'Anular Servicios',       desc: 'Permitir anular servicios no liquidados ni facturados' },
    ],
  },
  {
    id: 'admin',
    label: '💼 Acciones Administrativas',
    description: 'Funciones de facturación y liquidación',
    permissions: [
      { key: 'can_prefacturar', label: 'Pre-Facturar',    desc: 'Emitir pre-facturas a clientes' },
      { key: 'can_facturar',    label: 'Facturar',         desc: 'Emitir facturas definitivas' },
      { key: 'can_liquidar',    label: 'Liquidar',         desc: 'Procesar liquidaciones de conductores' },
    ],
  },
];

// Permisos por defecto cuando se asigna el rol locutor
const DEFAULT_LOCUTOR_PERMISSIONS = {
  can_view_dispatch: true, can_view_control_room: true,
  can_view_billing: false, can_view_insights: false,
  can_view_settlements: false, can_view_reports: false,
  can_view_clients: false, can_view_fleet: true,
  can_view_fleet_mgmt: false,
  can_view_tariffs: false, can_view_drivers: true,
  can_view_admin_trips: false, can_view_booking_sim: false,
  can_process_vouchers: false, can_create_trips: true,
  can_assign_vehicles: false, can_prefacturar: false,
  can_facturar: false, can_liquidar: false,
  can_void_trips: false,
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const ROLE_META = {
  admin:      { label: 'Administrador',    color: '#6366f1', bg: '#eef2ff', icon: <Shield size={13}/> },
  gestor:     { label: 'Gestor',           color: '#8b5cf6', bg: '#f5f3ff', icon: <Briefcase size={13}/> },
  operaciones:{ label: 'Jefa Operaciones', color: '#0ea5e9', bg: '#f0f9ff', icon: <Activity size={13}/> },
  locutor:    { label: 'Locutor',          color: '#f59e0b', bg: '#fffbeb', icon: <Activity size={13}/> },
  driver:     { label: 'Conductor',        color: '#10b981', bg: '#ecfdf5', icon: <Truck size={13}/> },
  passenger:  { label: 'Pasajero',         color: '#94a3b8', bg: '#f8fafc', icon: <Users size={13}/> },
  provider:   { label: 'Proveedor',        color: '#a855f7', bg: '#faf5ff', icon: <Briefcase size={13}/> },
};

const getRoleMeta = (role) => ROLE_META[role] || { label: role || 'Sin rol', color: '#94a3b8', bg: '#f8fafc', icon: <Users size={13}/> };

// ─── TOGGLE SWITCH COMPONENT ──────────────────────────────────────────────────
const PermToggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    style={{
      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? '#6366f1' : '#e2e8f0',
      transition: 'all 0.2s ease', position: 'relative', flexShrink: 0,
      opacity: disabled ? 0.4 : 1,
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: checked ? 23 : 3,
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ProfileManager() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editedPerms, setEditedPerms] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const canEdit = ['admin', 'gestor'].includes(currentUser?.role);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/users/', { params: { limit: 1000 } });
      setUsers(res.data || []);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Calcular permisos efectivos (pueden ser null para admin = acceso total)
  const getEffectivePerms = (u) => {
    if (!u) return {};
    if (['admin', 'gestor', 'operaciones'].includes(u.role)) return null; // Acceso total
    if (u.permissions) return u.permissions;
    // Usar defaults según rol
    return u.role === 'locutor' ? { ...DEFAULT_LOCUTOR_PERMISSIONS } : {};
  };

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    const perms = getEffectivePerms(u);
    setEditedPerms(perms ? { ...perms } : {});
  };

  const handleToggle = (key, val) => {
    setEditedPerms(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await apiClient.put(`/users/${selectedUser.id}/`, {
        permissions: editedPerms,
      });
      toast.success(`✅ Permisos de ${selectedUser.name || selectedUser.email} actualizados`);
      // Actualizar la lista local
      setUsers(prev => prev.map(u =>
        u.id === selectedUser.id ? { ...u, permissions: editedPerms } : u
      ));
      setSelectedUser(prev => ({ ...prev, permissions: editedPerms }));
    } catch (err) {
      toast.error('Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    if (!selectedUser) return;
    const defaults = selectedUser.role === 'locutor' ? { ...DEFAULT_LOCUTOR_PERMISSIONS } : {};
    setEditedPerms(defaults);
    toast.info('Permisos reseteados a valores por defecto (sin guardar)');
  };

  // Filtrar usuarios
  const filteredUsers = users.filter(u => {
    const matchSearch = !searchTerm ||
      (u.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const isAdminRole = selectedUser && ['admin', 'gestor', 'operaciones'].includes(selectedUser.role);

  // Count enabled permissions for a user
  const countEnabled = (u) => {
    const p = getEffectivePerms(u);
    if (p === null) return '∞';
    return Object.values(p).filter(Boolean).length;
  };

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 180px)', minHeight: 500 }}>

      {/* ── PANEL IZQUIERDO: Lista de usuarios ── */}
      <div style={{
        width: 340, flexShrink: 0, background: '#fff', borderRadius: 20,
        border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={18} color="#64748b" />
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Perfiles de Usuario</p>
              <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {filteredUsers.length} encontrados
              </p>
            </div>
            {loading && <RefreshCw size={14} color="#94a3b8" style={{ marginLeft: 'auto', animation: 'spin 1s linear infinite' }} />}
          </div>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="search"
              placeholder="Buscar usuario..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '8px 8px 8px 30px', borderRadius: 10,
                border: '1px solid #e2e8f0', fontSize: 12, outline: 'none',
                background: '#f8fafc', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Role filter */}
          <select
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
            style={{
              width: '100%', padding: '7px 10px', borderRadius: 10, border: '1px solid #e2e8f0',
              fontSize: 11, fontWeight: 700, outline: 'none', background: '#f8fafc', cursor: 'pointer',
            }}
          >
            <option value="all">Todos los roles</option>
            <option value="admin">Administrador</option>
            <option value="gestor">Gestor</option>
            <option value="operaciones">Jefa Operaciones</option>
            <option value="locutor">Locutor</option>
            <option value="driver">Conductor</option>
          </select>
        </div>

        {/* User list */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filteredUsers.map(u => {
            const meta = getRoleMeta(u.role);
            const isSelected = selectedUser?.id === u.id;
            const count = countEnabled(u);
            return (
              <button
                key={u.id}
                onClick={() => handleSelectUser(u)}
                style={{
                  width: '100%', textAlign: 'left', padding: '12px 20px',
                  border: 'none', cursor: 'pointer',
                  background: isSelected ? '#f8faff' : 'transparent',
                  borderLeft: isSelected ? '3px solid #6366f1' : '3px solid transparent',
                  transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 12,
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 38, height: 38, borderRadius: 12, background: meta.bg, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: meta.color,
                }}>
                  {meta.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: isSelected ? '#4f46e5' : '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {u.name || u.email}
                  </p>
                  <p style={{ margin: 0, fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {u.name ? u.email : `ID #${u.id}`}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                    background: meta.bg, color: meta.color, whiteSpace: 'nowrap',
                  }}>
                    {meta.label}
                  </span>
                  <span style={{ fontSize: 10, color: '#cbd5e1', fontWeight: 600 }}>
                    {count === '∞' ? '✨ Total' : `${count} permisos`}
                  </span>
                </div>
                <ChevronRight size={14} color={isSelected ? '#6366f1' : '#cbd5e1'} />
              </button>
            );
          })}
          {filteredUsers.length === 0 && !loading && (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Sin resultados</p>
            </div>
          )}
        </div>
      </div>

      {/* ── PANEL DERECHO: Editor de Permisos ── */}
      <div style={{
        flex: 1, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>
        {!selectedUser ? (
          // Estado vacío
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: '#cbd5e1' }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={28} color="#e2e8f0" />
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#94a3b8' }}>Selecciona un usuario</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#cbd5e1' }}>para ver y editar sus permisos</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header del editor */}
            <div style={{
              padding: '20px 28px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: isAdminRole ? '#faf5ff' : '#fafafa',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: getRoleMeta(selectedUser.role).bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: getRoleMeta(selectedUser.role).color, fontSize: 22,
                }}>
                  {getRoleMeta(selectedUser.role).icon}
                </div>
                <div>
                  <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
                    {selectedUser.name || selectedUser.email}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                      background: getRoleMeta(selectedUser.role).bg,
                      color: getRoleMeta(selectedUser.role).color,
                    }}>
                      {getRoleMeta(selectedUser.role).label}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{selectedUser.email}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!isAdminRole && canEdit && (
                  <>
                    <button
                      onClick={handleResetToDefaults}
                      style={{
                        padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0',
                        background: '#f8fafc', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#64748b',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <RefreshCw size={13}/> Reset
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      style={{
                        padding: '8px 18px', borderRadius: 10, border: 'none',
                        background: saving ? '#a5b4fc' : '#6366f1', color: '#fff',
                        cursor: saving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: 6,
                        boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                      }}
                    >
                      <Save size={13}/> {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelectedUser(null)}
                  style={{
                    width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0',
                    background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={15} color="#94a3b8"/>
                </button>
              </div>
            </div>

            {/* Contenido: permisos */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
              {isAdminRole ? (
                // Roles de administración: acceso total, sin configuración
                <div style={{
                  textAlign: 'center', padding: 40, background: '#f8f7ff', borderRadius: 16,
                  border: '1px dashed #c4b5fd',
                }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✨</div>
                  <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: '#6366f1' }}>Acceso Total</p>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8b5cf6' }}>
                    El rol <strong>{getRoleMeta(selectedUser.role).label}</strong> tiene acceso completo a todas las funciones del sistema. No se pueden restringir sus permisos.
                  </p>
                </div>
              ) : (
                // Editor de permisos granulares
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {PERMISSION_GROUPS.map(group => (
                    <div key={group.id}>
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: 13, color: '#334155' }}>{group.label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>{group.description}</p>
                      </div>
                      <div style={{
                        background: '#f8fafc', borderRadius: 14, border: '1px solid #f1f5f9',
                        overflow: 'hidden',
                      }}>
                        {group.permissions.map((perm, idx) => {
                          const isEnabled = editedPerms[perm.key] ?? false;
                          return (
                            <div
                              key={perm.key}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '13px 18px',
                                borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                                background: isEnabled ? '#fafafe' : 'transparent',
                                transition: 'background 0.15s',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                  background: isEnabled ? '#eef2ff' : '#f1f5f9',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  {isEnabled
                                    ? <Eye size={13} color="#6366f1" />
                                    : <EyeOff size={13} color="#cbd5e1" />
                                  }
                                </div>
                                <div>
                                  <p style={{ margin: 0, fontSize: 13, fontWeight: isEnabled ? 700 : 500, color: isEnabled ? '#1e293b' : '#94a3b8' }}>
                                    {perm.label}
                                  </p>
                                  <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{perm.desc}</p>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: isEnabled ? '#6366f1' : '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {isEnabled ? 'Habilitado' : 'Bloqueado'}
                                </span>
                                <PermToggle
                                  checked={isEnabled}
                                  onChange={(val) => handleToggle(perm.key, val)}
                                  disabled={!canEdit}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {!canEdit && (
                    <div style={{
                      padding: '12px 16px', background: '#fef3c7', borderRadius: 10,
                      border: '1px solid #fde68a', fontSize: 12, color: '#92400e', fontWeight: 600,
                    }}>
                      🔒 Solo los administradores pueden modificar permisos.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
